import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Plus, Trash2, Edit, X, Package, BedDouble } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";

// Unified manager for store products + hostel rooms.
// Modes: "store" or "hostel" — same UI shell, different fields + endpoints.

const MODES = {
  store: {
    title: "My Products",
    eyebrow: "Pet Store · Inventory",
    addLabel: "Add product",
    emptyLabel: "No products yet. Click 'Add product' to list your first item.",
    endpointPath: `/partner/store/products`,
    itemKey: "product_id",
    itemNoun: "product",
    fields: [
      { k: "name", label: "Name *", type: "text", required: true },
      { k: "category", label: "Category", type: "select", options: ["food", "toys", "grooming", "health", "accessories"], required: false },
      { k: "price", label: "Price (₹) *", type: "number", required: true },
      { k: "stock", label: "Stock", type: "number", required: false },
      { k: "description", label: "Description", type: "textarea" },
    ],
    DisplayBadge: ({ item }) => <span className="text-[10px] font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-0.5">{item.category || "—"}</span>,
    DisplayPrice: ({ item }) => <div className="font-heading text-xl font-extrabold">₹{item.price}</div>,
    DisplayMeta: ({ item }) => <div className="text-xs text-ink-soft">{item.stock ?? 0} in stock</div>,
    Icon: Package,
  },
  hostel: {
    title: "My Rooms",
    eyebrow: "Hostel · Boarding rooms",
    addLabel: "Add room",
    emptyLabel: "No rooms yet. Click 'Add room' to list your boarding capacity.",
    endpointPath: `/partner/hostel/rooms`,
    itemKey: "room_id",
    itemNoun: "room",
    fields: [
      { k: "name", label: "Room name *", type: "text", required: true },
      { k: "capacity", label: "Capacity (pets)", type: "number", required: false },
      { k: "price_per_day", label: "Price / day (₹) *", type: "number", required: true },
      { k: "description", label: "Description", type: "textarea" },
    ],
    DisplayBadge: ({ item }) => <span className="text-[10px] font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-0.5">cap {item.capacity ?? 1}</span>,
    DisplayPrice: ({ item }) => <div className="font-heading text-xl font-extrabold">₹{item.price_per_day}<span className="text-xs font-bold text-ink-soft">/day</span></div>,
    DisplayMeta: ({ item }) => <div className={`text-xs font-bold ${item.is_available !== false ? "text-brand-green" : "text-ink-soft"}`}>{item.is_available !== false ? "● Available" : "○ Unavailable"}</div>,
    Icon: BedDouble,
  },
};

export default function PartnerCatalog({ mode = "store" }) {
  const { user } = useAuth();
  const cfg = MODES[mode];
  const endpoint = `${API}${cfg.endpointPath}`;
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(endpoint).then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode]);

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${cfg.itemNoun}?`)) return;
    await axios.delete(`${endpoint}/${item[cfg.itemKey]}`);
    load();
  };

  const expectedRoles = mode === "store" ? ["store_owner", "super_admin"] : ["hostel_owner", "super_admin"];
  if (user && !expectedRoles.includes(user.role)) return (
    <div className="bg-surface border border-line rounded-3xl p-8 text-center">
      <h2 className="font-heading text-2xl font-extrabold">{mode === "store" ? "Store" : "Hostel"} account required</h2>
      <p className="text-ink-soft mt-2">Onboard your business from /app/partner to manage {cfg.itemNoun}s.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid={`partner-${mode}-page`}>
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">{cfg.eyebrow}</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">{cfg.title}</h1>
        </div>
        <button onClick={() => setCreating(true)} data-testid={`add-${mode}-btn`} className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> {cfg.addLabel}
        </button>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(it => (
          <article key={it[cfg.itemKey]} data-testid={`item-${it[cfg.itemKey]}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft">
            <div className="relative h-40 bg-surface-muted">
              {it.photo ? <img src={it.photo} alt={it.name} className="w-full h-full object-cover"/> : <div className="w-full h-full grid place-items-center text-ink-soft"><cfg.Icon className="w-10 h-10 opacity-40"/></div>}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-heading text-lg font-extrabold leading-tight">{it.name}</h3>
                  <div className="mt-1"><cfg.DisplayBadge item={it}/></div>
                </div>
                <cfg.DisplayPrice item={it}/>
              </div>
              <p className="text-xs text-ink-soft mt-2 line-clamp-2">{it.description}</p>
              <cfg.DisplayMeta item={it}/>
              <div className="mt-3 flex gap-1.5">
                <button onClick={() => setEditing(it)} className="flex-1 bg-surface-muted rounded-full py-1.5 text-xs font-bold inline-flex items-center justify-center gap-1"><Edit className="w-3 h-3"/> Edit</button>
                <button data-testid={`delete-${it[cfg.itemKey]}`} onClick={() => remove(it)} className="px-3 bg-surface-muted rounded-full text-xs font-bold hover:bg-brand hover:text-white"><Trash2 className="w-3 h-3"/></button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 && (
        <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">{cfg.emptyLabel}</div>
      )}
      {creating && <ItemModal cfg={cfg} endpoint={endpoint} onClose={() => { setCreating(false); load(); }}/>}
      {editing && <ItemModal cfg={cfg} endpoint={endpoint} item={editing} onClose={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function ItemModal({ cfg, endpoint, item, onClose }) {
  const isEdit = !!item;
  const numericKeys = cfg.fields.filter(f => f.type === "number").map(f => f.k);
  const defaults = { name: "", photo: null, description: "", is_available: true };
  numericKeys.forEach(k => { defaults[k] = 0; });
  const [form, setForm] = useState(item || defaults);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await axios.patch(`${endpoint}/${item[cfg.itemKey]}`, form);
      else await axios.post(endpoint, form);
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="item-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-extrabold">{isEdit ? "Edit" : "Add"} {cfg.itemNoun}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <PhotoUpload value={form.photo} onChange={p => set("photo", p)} label={`${cfg.itemNoun} photo`} testid="item-photo"/>
        {cfg.fields.map(f => (
          <label key={f.k} className="block mt-3">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{f.label}</span>
            {f.type === "select" ? (
              <select required={f.required} value={form[f.k] || ""} onChange={e => set(f.k, e.target.value)} className="inp">
                <option value="">—</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === "textarea" ? (
              <textarea rows={3} required={f.required} value={form[f.k] || ""} onChange={e => set(f.k, e.target.value)} className="inp"/>
            ) : (
              <input data-testid={`field-${f.k}`} type={f.type} required={f.required} value={form[f.k] ?? ""} onChange={e => {
                if (f.type === "number") {
                  const n = Number(e.target.value);
                  set(f.k, Number.isFinite(n) ? n : 0);
                } else { set(f.k, e.target.value); }
              }} className="inp"/>
            )}
          </label>
        ))}
        {cfg.itemNoun === "room" && (
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={form.is_available !== false} onChange={e => set("is_available", e.target.checked)}/>
            Currently available
          </label>
        )}
        <button data-testid="submit-item" disabled={saving} className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">{saving ? "Saving…" : isEdit ? "Save changes" : `Add ${cfg.itemNoun}`}</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;margin-top:0.25rem;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}
